import React, { useState, useMemo } from 'react';
import { useBetting } from '../../context/BettingContext';
import { COMPETITIONS, MARKET_TYPES } from '../../data/mockData';
import BettingCard from './BettingCard';
import { useMasterEvent } from '../../context/MasterEventContext';
import { Link, useParams } from 'react-router-dom';
import HelpTip from '../shared/HelpTip';
import './Betting.css';

export default function BettingPage() {
  const { activities, isEventAdmin, isActivityAdmin } = useMasterEvent();
  const { id, actId } = useParams();
  const { events, rounds, markets, loading, isTeamBased: serverTeamBased, participant, balance } = useBetting();
  // The server's mode wins over a stale activity list.
  const isTeamBased = serverTeamBased ?? activities.find(a => a.id === actId)?.isTeamBased;
  const [selectedRound, setSelectedRound] = useState(null);
  const [filterComp, setFilterComp] = useState('all');
  const [filterType, setFilterType] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');

  const activeEvent = events.find((e) => e.status === 'active') || events[0];
  const eventRounds = rounds
    .filter((r) => r.eventId === activeEvent?.id)
    .sort((a, b) => a.roundNumber - b.roundNumber);

  const activeRound = selectedRound
    ? eventRounds.find((r) => r.id === selectedRound)
    : eventRounds.find((r) => r.status === 'open') || eventRounds[0];

  const filteredMarkets = useMemo(() => {
    let result = markets.filter((m) => m.roundId === activeRound?.id);
    if (filterComp !== 'all') result = result.filter((m) => m.competition === filterComp);
    if (filterType !== 'all') result = result.filter((m) => m.type === filterType);
    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      result = result.filter((m) => m.matchName.toLowerCase().includes(term));
    }
    return result;
  }, [markets, activeRound, filterComp, filterType, searchTerm]);

  const groupedByMatch = useMemo(() => {
    const groups = {};
    filteredMarkets.forEach((m) => {
      if (!groups[m.matchName]) groups[m.matchName] = [];
      groups[m.matchName].push(m);
    });
    return groups;
  }, [filteredMarkets]);

  if (loading) {
    return <div className="betting-loading">Loading markets...</div>;
  }

  const roundStatusIcon = (status) => {
    if (status === 'open') return '🟢';
    if (status === 'closed') return '🔴';
    if (status === 'concluded') return '✅';
    return '🟡';
  };

  return (
    <div className="betting-page">
      <div className="betting-header">
        <h2>Place Your Bets</h2>
        {activeEvent && (
          <p className="betting-header-sub">
            {activeEvent.name} — Round {activeRound?.roundNumber || '?'} of {activeEvent.totalRounds}
          </p>
        )}
      </div>

      {isTeamBased && (participant ? (
        <div className="betting-team-banner">
          <span>👥 Betting for <strong>{participant.name}</strong></span>
          <span>· Team balance: <strong>{(balance || 0).toLocaleString()} coins</strong></span>
          <HelpTip label="Team betting">This is a team activity. Every member of your team places their own bets, and all stakes come from one shared team wallet. Winnings go back into it. Your teammates' bets show on each market.</HelpTip>
        </div>
      ) : (
        <div role="status" className="mea-error">
          This is a team activity. An admin needs to assign you to a team before you can bet; your personal balance cannot be used here.
          {isEventAdmin && <> <Link to={`/me/${id}/admin`}>Create teams and assign yourself</Link>.</>}
          {(isEventAdmin || isActivityAdmin) && <> You can also edit the activity to turn off Team-based for individual testing.</>}
        </div>
      ))}

      {/* Round selector */}
      <div className="betting-events">
        {eventRounds.map((rnd) => (
          <button
            key={rnd.id}
            className={`betting-event-btn ${activeRound?.id === rnd.id ? 'betting-event-btn--active' : ''}`}
            onClick={() => setSelectedRound(rnd.id)}
          >
            <span className="betting-event-status">{roundStatusIcon(rnd.status)}</span>
            <div>
              <div className="betting-event-name">{rnd.name}</div>
              <div className="betting-event-dates">
                {rnd.startDate && new Date(rnd.startDate).toLocaleDateString('en-GB', { day: 'numeric', month: 'short' })}
                {rnd.endDate && <> - {new Date(rnd.endDate).toLocaleDateString('en-GB', { day: 'numeric', month: 'short' })}</>}
              </div>
            </div>
          </button>
        ))}
      </div>

      {/* Filters */}
      <div className="betting-filters">
        <input
          type="text"
          className="betting-search"
          placeholder="Search matches..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
        />
        <select className="betting-filter-select" value={filterComp} onChange={(e) => setFilterComp(e.target.value)}>
          <option value="all">All Competitions</option>
          {Object.values(COMPETITIONS).map((c) => (
            <option key={c.id} value={c.id}>{c.emoji} {c.name}</option>
          ))}
        </select>
        <select className="betting-filter-select" value={filterType} onChange={(e) => setFilterType(e.target.value)}>
          <option value="all">All Market Types</option>
          {Object.values(MARKET_TYPES).map((t) => (
            <option key={t.id} value={t.id}>{t.icon} {t.name}</option>
          ))}
        </select>
      </div>

      {/* Markets */}
      {Object.keys(groupedByMatch).length === 0 ? (
        <div className="betting-empty">
          <span className="betting-empty-icon">🔍</span>
          <p>No markets found for this round.</p>
        </div>
      ) : (
        <div className="betting-groups">
          {Object.entries(groupedByMatch).map(([matchName, matchMarkets]) => (
            <div key={matchName} className="betting-match-group">
              <h3 className="betting-match-group-title">{matchName}</h3>
              <div className="betting-cards-grid">
                {matchMarkets.map((market) => (
                  <BettingCard key={market.id} market={market} />
                ))}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
